import { MenuItem, SxProps, TextField } from '@mui/material';
import { useRouter } from '@tanstack/react-router';
import { startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useViewer } from '../../context/viewer';

const BASE_LIBRARY_ID = 'base-library';

export function TeamSelect({
  teamId,
  onSelect,
  sx,
  showBaseLibrary = false,
  label,
  disabled = false,
}: {
  sx?: SxProps;
  teamId?: string | null;
  onSelect?: (teamId: string) => void;
  showBaseLibrary?: boolean;
  label?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    team: { setSelectedTeam, teams },
    org: {
      settings: { showBaseLibrary: orgShowBaseLibrary },
    },
  } = useViewer();

  if (!teams || teams.length <= 1) {
    return null;
  }

  const sortedTeams = [...teams].sort((a, b) => {
    // "coaches" team always comes first
    if (a.name === 'coaches') return -1;
    if (b.name === 'coaches') return 1;
    return a.name.localeCompare(b.name);
  });

  let teamOptions = sortedTeams;
  if (showBaseLibrary && orgShowBaseLibrary) {
    teamOptions = [
      { id: BASE_LIBRARY_ID, name: t('baseLibrary') },
      ...sortedTeams,
    ];
  }

  const selectValue = !teamId && orgShowBaseLibrary ? BASE_LIBRARY_ID : teamId;

  return (
    <TextField
      sx={sx}
      select
      disabled={disabled}
      value={selectValue}
      inputProps={{ 'aria-label': label?.trim() || t('selectTeam') }}
      onChange={(e) => {
        const value = e.target.value;
        const newTeamId = value === BASE_LIBRARY_ID ? '' : value;
        // setSelectedTeam re-renders the team-scoped subtree, which suspends on
        // the new team's data. React hides a subtree that suspends on a
        // synchronous update, detaching refs while leaving the DOM in place,
        // which crashed the still-focused MUI MenuItem's keydown handler
        // (SC-4096). Router navigation is already transition-wrapped by
        // commitLocation; setSelectedTeam is the update that was not.
        startTransition(() => {
          setSelectedTeam(newTeamId);
          if (onSelect) {
            onSelect(newTeamId);
          } else {
            // Replace the teamId segment in the current path. router.navigate without
            // `to` doesn't substitute path params in TanStack Router v1, so we build
            // the new path explicitly. teamId is always the first path segment, so
            // swap it by index rather than building a RegExp from a route value,
            // which Snyk flags as a ReDoS risk.
            const segments = router.state.location.pathname.split('/');
            if (teamId && newTeamId && segments[1] === teamId) {
              segments[1] = newTeamId;
            }
            const newPath = segments.join('/');
            // OrgWithNavLayout subscribes to router 'onResolved' and invalidates queries
            // after the URL update is committed — no need to chain invalidation here.
            router.history.push(newPath + router.state.location.searchStr);
          }
        });
      }}
      label={label}
      SelectProps={{
        SelectDisplayProps: {
          'aria-label': t('selectTeam'),
        },
      }}
    >
      {teamOptions.map((team) => (
        <MenuItem key={team.id || BASE_LIBRARY_ID} value={team.id}>
          {t(team.name as any)}
        </MenuItem>
      ))}
    </TextField>
  );
}
